import { useEffect, useReducer, useState } from 'react'
import { Routes, Route, Navigate, useMatch } from 'react-router-dom'
import './portal.css'
import { getToken, setToken, clearToken, getMe, requestMagicLink, verifyMagicLink } from './api'
import type { PortalMeResult } from './api'
import Nav from './components/Nav'
import Login from './screens/Login'
import Dashboard from './screens/Dashboard'
import Roadmap from './screens/Roadmap'
import Notifications from './screens/Notifications'
import SupportFloat from './components/SupportFloat'

// ── Auth state machine ────────────────────────────────────────────────────────

type AppState =
  | { tag: 'loading' }
  | { tag: 'login'; logoUrl?: string | null }
  | { tag: 'link-sent'; email: string; logoUrl?: string | null }
  | { tag: 'ready'; me: PortalMeResult }

type AppAction =
  | { type: 'LOADED_ME'; me: PortalMeResult }
  | { type: 'LOAD_FAILED' }
  | { type: 'LINK_SENT'; email: string; logoUrl?: string | null }
  | { type: 'SIGNED_OUT' }

function reducer(_state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'LOADED_ME':
      return { tag: 'ready', me: action.me }
    case 'LOAD_FAILED':
      return { tag: 'login' }
    case 'LINK_SENT':
      return { tag: 'link-sent', email: action.email, logoUrl: action.logoUrl }
    case 'SIGNED_OUT':
      return { tag: 'login' }
  }
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  const [appState, dispatch] = useReducer(reducer, { tag: 'loading' })
  const [initDone, setInitDone] = useState(false)

  // Read enrolleeId from URL for SupportFloat
  const roadmapMatch = useMatch('/roadmap/:enrolleeId')
  const supportEnrolleeId = roadmapMatch?.params.enrolleeId

  useEffect(() => {
    void init()
  }, [])

  async function init() {
    // Exchange magic-link token from URL if present.
    const params = new URLSearchParams(window.location.search)
    const urlToken = params.get('t')
    if (urlToken) {
      // Strip token from URL immediately (privacy).
      params.delete('t')
      const newSearch = params.toString()
      window.history.replaceState({}, '', newSearch ? `?${newSearch}` : window.location.pathname)
      try {
        const result = await verifyMagicLink(urlToken)
        if (result.ok && result.sessionToken) {
          setToken(result.sessionToken)
        } else {
          dispatch({ type: 'LOAD_FAILED' })
          setInitDone(true)
          return
        }
      } catch {
        dispatch({ type: 'LOAD_FAILED' })
        setInitDone(true)
        return
      }
    }

    // Try to load me with stored token.
    const token = getToken()
    if (!token) {
      dispatch({ type: 'LOAD_FAILED' })
      setInitDone(true)
      return
    }

    try {
      const me = await getMe()
      dispatch({ type: 'LOADED_ME', me })
    } catch {
      clearToken()
      dispatch({ type: 'LOAD_FAILED' })
    } finally {
      setInitDone(true)
    }
  }

  function handleSignOut() {
    clearToken()
    dispatch({ type: 'SIGNED_OUT' })
  }

  async function handleLinkRequest(email: string) {
    await requestMagicLink(email)
    const logoUrl = appState.tag === 'ready' ? appState.me.brand.portalLogoUrl : null
    dispatch({ type: 'LINK_SENT', email, logoUrl })
  }

  if (!initDone || appState.tag === 'loading') {
    return (
      <div className="p-center">
        <div className="p-spinner" />
      </div>
    )
  }

  const logoUrl = appState.tag === 'ready'
    ? appState.me.brand.portalLogoUrl
    : null

  if (appState.tag === 'link-sent') {
    return (
      <div className="p-login-wrap">
        <div className="p-login-card p-login-card-accent">
          <div className="p-login-logo">
            {appState.logoUrl
              ? <img src={appState.logoUrl} alt="The Successful Bookkeeper" />
              : <span className="p-login-brand-name">The Successful Bookkeeper</span>
            }
          </div>
          <div className="p-check-inbox">
            <div className="p-inbox-icon">
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="36" height="36" rx="10" fill="#FEF0EB"/>
                <path d="M9 13a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H11a2 2 0 01-2-2V13z" stroke="#F05A28" strokeWidth="1.5" fill="none"/>
                <path d="M9 14l9 6 9-6" stroke="#F05A28" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <h2>Check your inbox</h2>
            <p>We sent a sign-in link to <strong>{appState.email}</strong>. Click it to continue.</p>
            <button
              className="resend"
              onClick={() => handleLinkRequest(appState.email).catch(() => {})}
            >
              Resend link
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (appState.tag === 'login') {
    return <Login logoUrl={logoUrl} onLinkSent={(email) => dispatch({ type: 'LINK_SENT', email })} />
  }

  // Authenticated
  const { me } = appState

  return (
    <>
      <Nav
        logoUrl={me.brand.portalLogoUrl}
        firstName={me.person.first_name}
        email={me.person.primary_email}
        onSignOut={handleSignOut}
      />

      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard me={me} />} />
        <Route path="/roadmap/:enrolleeId" element={<Roadmap />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>

      <SupportFloat enrolleeId={supportEnrolleeId} />
    </>
  )
}
